import type { FunctionComponent } from "react"

import Image from "next/image"

import IconBell from "@jobteaser/ui-kit/assets/icons/jds_bell.svg"
import IconFire from "@jobteaser/ui-kit/assets/icons/jds_fire.svg"
import { EventCard } from "@jobteaser/ui-kit/components/Card/cardComponents/EventCard/EventCard"
import { ElementGrid } from "@jobteaser/ui-kit/components/ElementGrid/ElementGrid"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { useCompanyEventsFetch } from "@/modules/companies/client/hooks/useCompanyEventsFetch"
import { getEventCardTopTag } from "@/modules/companies/client/utils/manageEventCardTopTag"
import { formatRange } from "@/modules/dates/dates"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { getDisplayableLocation } from "@/modules/locations/locations"

import styles from "./EventListing.module.css"

export const EventListing: FunctionComponent = () => {
  const locale = useLocale()
  const { t } = useTranslation(["shared_events"])
  const { t: tDateTime } = useTranslation(["shared_date_time"])
  const { dataCompanyEvents } = useCompanyEventsFetch()
  const { dataCareerCenter } = useCareerCenterFetch()

  return (
    <ElementGrid wrapperTag="ul" itemTag="li">
      {dataCompanyEvents?.events.map(event => {
        let locationText = ""
        if (event.virtual) locationText = t(`shared_events.online`)
        if (event.location) locationText = getDisplayableLocation(event.location)

        const { showLessThanThreeDaysTopTag, showLastSlotsTopTag } = getEventCardTopTag(event)

        return (
          <EventCard
            key={event.id}
            title={event.title}
            companyName={event.organizerName}
            dateText={formatRange({
              endDate: event.endDate,
              locale,
              startDate: event.startDate,
              t: tDateTime,
              timeZone: "Europe/Paris",
            })}
            imageSrc={event.imageUrl}
            imageProps={{
              component: Image,
              height: 9,
              sizes: "(max-width: 768px) 100vw, (max-width: 1024x) 50vw, 33vw",
              /* random 16 / 9 size, resize is done after on css side */
              width: 16,
            }}
            locationText={locationText}
            tagText={t(`shared_events.tags.${event.tags[0]}`)}
            companyLogo={event.organizerLogo}
            logoProps={{
              component: Image,
              height: 64,
              sizes: "64px",
              width: 64,
            }}
            titleTag="h2"
            linkProps={{
              as: "a",
              href: `/${locale}/events/${event.slug}`,
            }}
            isFullHeight
            className={styles.eventCard} // Tmp: waiting update ui-shift
          >
            {event.private && dataCareerCenter?.logo && dataCareerCenter?.name && (
              <EventCard.Signature imgSrc={dataCareerCenter.logo}>{dataCareerCenter.name}</EventCard.Signature>
            )}
            {showLastSlotsTopTag && (
              <EventCard.TopTag icon={`#${IconFire.id}`} color="purple">
                {t("shared_events.topTag.lastSpots")}
              </EventCard.TopTag>
            )}
            {showLessThanThreeDaysTopTag && (
              <EventCard.TopTag icon={`#${IconBell.id}`} color="warning">
                {t("shared_events.topTag.lastDays")}
              </EventCard.TopTag>
            )}
          </EventCard>
        )
      })}
    </ElementGrid>
  )
}
